/**
 * v1 Users API Client
 * Direct integration with SAIFChat users and balance collections
 */

import { apiClient } from '@/lib/api-client'
import { API_CONFIG } from '@/lib/config'

const BASE_URL = API_CONFIG.BASE

/**
 * User interface (SAIFChat user format)
 */
export interface User {
  _id: string
  email: string
  name?: string
  username?: string
  role: string
  provider?: string
  emailVerified?: boolean
  avatar?: string
  tokenCredits?: number
  total_agents?: number
  tokens_consumed?: number
  cost?: number
  balance?: Balance
  createdAt?: string
  updatedAt?: string
}

/**
 * Balance interface (SAIFChat Balance collection format)
 */
export interface Balance {
  _id: string
  user: string
  tokenCredits: number
  autoRefillEnabled?: boolean
  refillAmount?: number
  refillIntervalValue?: number
  refillIntervalUnit?: string
  lastRefill?: string
  createdAt?: string
  updatedAt?: string
}

/**
 * Top-up request payload
 */
export interface TopUpRequest {
  amount: number
}

/**
 * User list response
 */
export interface UserListResponse {
  users: User[]
  total: number
  page: number
  limit: number
  totalPages: number
  has_more: boolean
  has_previous: boolean
}

/**
 * Top-up response
 */
export interface TopUpResponse {
  user_id: string
  user_email: string
  amount_added: number
  new_balance: number
  previous_balance: number
  updated_at: string
}

/**
 * Refill settings request payload
 */
export interface RefillSettingsRequest {
  autoRefillEnabled: boolean
  refillAmount?: number
  refillIntervalValue?: number
  refillIntervalUnit?: 'days' | 'weeks' | 'months'
}

/**
 * Refill settings response
 */
export interface RefillSettingsResponse {
  user_id: string
  user_email: string
  autoRefillEnabled: boolean
  refillAmount?: number
  refillIntervalValue?: number
  refillIntervalUnit?: string
  lastRefill?: string
  tokenCredits: number
  updated_at: string
}

/**
 * v1 Users API
 */
export const v1UsersApi = {
  /**
   * Get list of all SAIFChat users with balances
   */
  getUsers: async (page = 1, limit = 10, role?: string, sortBy?: string, sortOrder?: string, email?: string): Promise<UserListResponse> => {
    const params = new URLSearchParams({
      page: String(page),
      limit: String(limit)
    })

    if (role) {
      params.append('role', role)
    }

    if (sortBy) {
      params.append('sort_by', sortBy)
    }

    if (sortOrder) {
      params.append('sort_order', sortOrder)
    }

    if (email) {
      params.append('email', email)
    }

    const response = await apiClient.get(`${BASE_URL}/users?${params}`)
    return response.data.data
  },

  /**
   * Get detailed information for a specific user
   */
  getUserById: async (id: string): Promise<User> => {
    const response = await apiClient.get(`${BASE_URL}/users/${id}`)
    return response.data.data
  },

  /**
   * Get balance for a specific user
   */
  getUserBalance: async (id: string): Promise<Balance> => {
    const response = await apiClient.get(`${BASE_URL}/users/${id}/balance`)
    return response.data.data
  },

  /**
   * Top up user balance
   */
  topUpBalance: async (id: string, data: TopUpRequest): Promise<TopUpResponse> => {
    const response = await apiClient.post(`${BASE_URL}/users/${id}/topup`, data)
    return response.data.data
  },

  /**
   * Update refill settings for a user
   */
  updateRefillSettings: async (id: string, data: RefillSettingsRequest): Promise<RefillSettingsResponse> => {
    const response = await apiClient.put(`${BASE_URL}/users/${id}/refill-settings`, data)
    return response.data.data
  },

  /**
   * Get user config flags
   */
  getUserConfig: async (userId: string): Promise<Record<string, boolean>> => {
    const response = await apiClient.get(`${BASE_URL}/user-config/${userId}`)
    return response.data.data
  },

  /**
   * Update user config flags (can update one or multiple at once)
   */
  updateUserConfig: async (
    userId: string,
    updates: Record<string, boolean>
  ): Promise<Record<string, boolean>> => {
    const response = await apiClient.put(`${BASE_URL}/user-config/${userId}`, {
      updates: updates
    })
    return response.data.data
  },

  /**
   * Export users to CSV
   * Returns a blob that can be downloaded
   */
  exportCSV: async (role?: string, email?: string): Promise<Blob> => {
    const params = new URLSearchParams()
    if (role) {
      params.append('role', role)
    }
    if (email) {
      params.append('email', email)
    }

    const response = await apiClient.get(`${BASE_URL}/users/export/csv?${params}`, {
      responseType: 'blob'
    })
    return response.data
  }
}
